import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
    Box,
    Dialog,
    Button,
    Skeleton,
    Divider,
    useTheme,
    DialogContent
} from "@mui/material";
import { useLazyIsUserFirstTimeLoginQuery } from "Services/AuthManagementService";
import { useGetUserProfileQuery } from "Services/UserProfileService";
import usBanner from "assets/images/us-banner.png";
import caBanner from "assets/images/canada-banner.png";
import grantLogo from "assets/images/ga-logo-sm.png";

import MessageContentUS, { getUSSlides } from "./MessageContentUS";
import MessageContentCA, { getCASlides } from "./MessageContentCA";
import { SliderIndicatorIcon } from "assets/images/icons/Components";
import { useTranslation } from "react-i18next";
import ReferralFormPopup from "layout/MainLayout/Header/ReferralSection/ReferralForm.Popup";
import ReferCharityPopup from "layout/MainLayout/Header/ReferralSection/ReferCharity.Popup";
import OnboardingPopup from "views/shared/OnboardingPopup";
import { useGetOnboardingPopupRemainderMutation, useSaveOnboardingPopupRemainderMutation } from "Services/OrganizationProfileService";

export default function WelcomeMessage() {
    const [open, setOpen] = useState(false);
    const [trigger] = useLazyIsUserFirstTimeLoginQuery();
    const { userToken } = useSelector((state) => state.sharedState);
    const [apiData, setApiData] = useState(null);

    const [ getRemainder, { isLoading: isGetting } ] = useGetOnboardingPopupRemainderMutation();
    const [ saveRemainder, { isLoading: isSaving } ] = useSaveOnboardingPopupRemainderMutation()

    const defaultFormValue = {
        nameCharity: { value: "", error: "" },
        nameContact: { value: "", error: "" },
        email: { value: "", error: "" },
        phoneNumber: { value: "", error: "" },
        success: { value: "", error: "" },
    };
    const [formValue, setFormValue] = useState(defaultFormValue);

    const [openPopupReferral, setOpenPopupReferral] = useState(false);

    const [ showPopupOnboarding, setShowPopupOnboarding ] = useState(false);
    const [ shouldOpenPopupOnboarding, setShouldOpenPopupOnboarding ] = useState(false);
    const [ doNotShowAgain, setDoNotShowAgain ] = useState(false);

    const handleOpenPopupReferral = () => {
        setFormValue(defaultFormValue);
        setOpenPopupReferral(true);
    };

    const handleClosePopupReferral = () => {
        setOpenPopupReferral(false);
    };

    useEffect(() => {        
        if(!userToken?.Id) return;
        
        const hasSeenWelcome = localStorage.getItem(`welcomeShown_${userToken?.Id}`);
        
        if (hasSeenWelcome === "true") {
            handleOpenOnboardingPopup();
            return;
        }
        
        const timeoutId = setTimeout(() => {
            trigger()
                .unwrap()
                .then((res) => {
                    if (res.success === true && res.showMessage === true) {
                        setApiData(res);
                        setOpen(true);
                        
                        localStorage.setItem(`welcomeShown_${userToken?.Id}`, "true");
                    }

                    setShouldOpenPopupOnboarding(res.showOnboardingPopup);

                    if (res.showOnboardingPopup === true) {
                        handleOpenOnboardingPopup();
                    }
                })
                .catch(() => {
                    // Don't show modal if API fails
                });
        }, 1500);

        return () => clearTimeout(timeoutId);
    }, [trigger, userToken?.Id]);

    const handleClose = () => {
        setOpen(false);

        if (shouldOpenPopupOnboarding) {
            window.setTimeout(() => {
                setShowPopupOnboarding(true);
            }, 200);
        }
        
    };

    const handleOpenReferal = () => {
        handleOpenPopupReferral();
        handleClose();
    }

    const handleCloseOnboardingPopup = () => {

        saveRemainder(false).unwrap().then(response => {
            setShowPopupOnboarding(false);
        })
    }

    const handleDoNotShowAgain = () => {

        saveRemainder(true).unwrap().then(response => {
            setShowPopupOnboarding(false);
        })
    }

    
    const handleOpenOnboardingPopup = () => {

        getRemainder().unwrap().then(response => {
            setShowPopupOnboarding(response.showDialog);
            setDoNotShowAgain(response.doNotShowAgain);
        })
    }

    return (
        <>
            <WelcomeMessagePopup 
                open={open} 
                onClose={handleClose}
                apiData={apiData}
                onClickOpenReferralPopup={handleOpenReferal}
            />

            <ReferralFormPopup
                openPopup={openPopupReferral}
                onClose={handleClosePopupReferral}
                width="600px"
                height="400px"
                formValue={formValue}
                setFormValue={setFormValue}
            />

            <OnboardingPopup 
                onClose={handleCloseOnboardingPopup}
                open={showPopupOnboarding}
                doNotShowAgain={doNotShowAgain}
                onNotShowAgainClick={handleDoNotShowAgain}
                isSavingRemainder={isSaving}
            />

        </>
        
    );
}

export function WelcomeMessagePopup({ open, onClose, onClickOpenReferralPopup, showCloseButton = true, apiData = null,  }) {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const [currentSlide, setCurrentSlide] = useState(0);
    const { userToken } = useSelector((state) => state.sharedState);
    const { data: userProfile, isFetching } = useGetUserProfileQuery(undefined, { skip: !open });
    const [languageSet, setLanguageSet] = useState(false);
    const [openReferralDialog, setOpenReferralDialog] = useState(false);
   
    let country = "AccessUs";
    let userRoles = [];
        
    if (Array.isArray(userToken?.role)) {
        userRoles = userToken?.role ?? ["User"];
    } else {
        userRoles.push(userToken?.role ?? "User");
    }

    const accessUs = userRoles.filter((string) =>
        string.toUpperCase().includes("AccessUs".toUpperCase())
    );

    const accessCanada = userRoles.filter((string) =>
        string.toUpperCase().includes("AccessCanada".toUpperCase())
    );

    if (accessCanada.length > 0) {
        country = "AccessCa"; 
    } else if (accessUs.length > 0) {
        country = "AccessUs";
    }

    const isCA = country === "AccessCa";

    useEffect(() => {
        if (!languageSet) {
            const lang = isCA ? "en-CA" : "en-US";
            i18n.changeLanguage(lang);
            setLanguageSet(true);
        }
    }, [isCA, i18n, languageSet]);

    const slides = isCA ? getCASlides(t) : getUSSlides(t);
    const bannerImage = isCA ? caBanner : usBanner;

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    };

    const showFullContent = !showCloseButton;

    if (showFullContent) {
        return (
            <Dialog open={open} maxWidth="md" fullWidth>
                <DialogContent sx={{ p: 4 }}>
                    {isCA ? (
                        <MessageContentCA 
                            slideData={slides[currentSlide]} 
                            SlideIcon={slides[currentSlide]?.linkIcon}
                            userName={userProfile?.firstName}
                            onClickOpenReferral={onClickOpenReferralPopup}
                        />
                    ) : (
                        <MessageContentUS 
                            slideData={slides[currentSlide]} 
                            SlideIcon={slides[currentSlide]?.linkIcon}
                            userName={userProfile?.firstName}
                            onClickOpenReferral={onClickOpenReferralPopup}
                        />
                    )}
                    <Box textAlign="center" mt={4}>
                        <Button
                            variant="contained"
                            color="secondary"
                            sx={{ px: 4, borderRadius: 2 }}
                            onClick={onClose}
                        >
                            Go To Dashboard
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    const SlideIcon = slides[currentSlide]?.linkIcon;
    const slideData = slides[currentSlide];

    const handleReferralClick = () => {
        setOpenReferralDialog(true);
    };

    return (
        <>
            <Dialog 
                open={open} 
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        width: 635,
                        maxWidth: '635px !important',
                        maxHeight: '90vh', 
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }
                }}
                data-testid="welcome-message-modal"
            >
                <Box sx={{ 
                    p: 3, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    height: '100%',
                    overflow: 'hidden',
                }}>
                    <Box sx={{
                        position: 'relative',
                        width: '100%',
                        height: 120,
                        mb: 2,
                        borderRadius: 1,
                        overflow: 'hidden',
                    }}>
                        <Box
                            component="img"
                            src={bannerImage}
                            alt="Country Banner"
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                        />
                        <Box sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                        }}>
                            <img 
                                src={grantLogo} 
                                alt="Grant Advance Logo" 
                                style={{ 
                                    width: '200px', 
                                    height: 'auto',
                                }}
                            />
                        </Box>
                    </Box>

                    {isFetching ? (
                        <Skeleton variant="rectangular" sx={{ flex: 1 }} />
                    ) : (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            flex: 1,
                            minHeight: 0, 
                        }}>
                            <Box sx={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0, 
                                overflow: 'hidden',
                            }}>
                                {isCA ? (
                                    <MessageContentCA 
                                        slideData={slideData} 
                                        SlideIcon={SlideIcon}
                                        userName={userProfile?.firstName}
                                        onClickOpenReferral={() => {
                                            onClickOpenReferralPopup();
                                        }}
                                    />
                                ) : (
                                    <MessageContentUS 
                                        slideData={slideData} 
                                        SlideIcon={SlideIcon}
                                        userName={userProfile?.firstName}
                                        onClickOpenReferral={() => {
                                            onClickOpenReferralPopup();
                                        }}
                                    />
                                )}
                            </Box>

                            <Box sx={{ flexShrink: 0, mt: 'auto' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                                    {slides.map((_, index) => (
                                        <Box key={index}>
                                            <SliderIndicatorIcon active={index === currentSlide} />
                                        </Box>
                                    ))}
                                </Box>

                                <Divider sx={{ mb: 2 }} />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Button
                                        variant="outlined"
                                        onClick={prevSlide}
                                        sx={{
                                            minWidth: 64,
                                            py: 1,
                                            px: 2.75,
                                            borderRadius: 1,
                                            fontFamily: 'Poppins',
                                            fontSize: '0.9375rem',
                                            fontWeight: 500,
                                            lineHeight: 1.75,
                                            textTransform: 'capitalize',
                                            color: '#009688',
                                            border: '1px solid #009688',
                                            boxShadow: '0 3px 1px -2px rgba(0,0,0,0.2), 0 2px 2px 0 rgba(0,0,0,0.14), 0 1px 5px 0 rgba(0,0,0,0.12)',
                                            '&:hover': {
                                                borderColor: '#009688',
                                                backgroundColor: 'rgba(0, 150, 136, 0.04)',
                                            },
                                        }}
                                    >
                                        Previous
                                    </Button>

                                    {currentSlide === slides.length - 1 ? (
                                        <Button
                                            variant="contained"
                                            onClick={onClose}
                                            data-testid="go-to-dashboard-btn"
                                            sx={{
                                                minWidth: 64,
                                                py: 1,
                                                px: 2.75,
                                                borderRadius: 1,
                                                fontFamily: 'Poppins',
                                                fontSize: '0.9375rem',
                                                fontWeight: 500,
                                                lineHeight: 1.75,
                                                textTransform: 'capitalize',
                                                backgroundColor: '#009688',
                                                color: '#FFF',
                                                boxShadow: '0 3px 1px -2px rgba(0,0,0,0.2), 0 2px 2px 0 rgba(0,0,0,0.14), 0 1px 5px 0 rgba(0,0,0,0.12)',
                                                '&:hover': {
                                                    backgroundColor: '#00897b',
                                                },
                                            }}
                                        >
                                            Close
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            onClick={nextSlide}
                                            sx={{
                                                minWidth: 64,
                                                py: 1,
                                                px: 2.75,
                                                borderRadius: 1,
                                                fontFamily: 'Poppins',
                                                fontSize: '0.9375rem',
                                                fontWeight: 500,
                                                lineHeight: 1.75,
                                                textTransform: 'capitalize',
                                                backgroundColor: '#009688',
                                                color: '#FFF',
                                                boxShadow: '0 3px 1px -2px rgba(0,0,0,0.2), 0 2px 2px 0 rgba(0,0,0,0.14), 0 1px 5px 0 rgba(0,0,0,0.12)',
                                                '&:hover': {
                                                    backgroundColor: '#00897b',
                                                },
                                            }}
                                        >
                                            Next
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Dialog>

            <ReferCharityPopup 
                openPopup={openReferralDialog}
                onClose={() => setOpenReferralDialog(false)}
            />

        </>
    );
}
